/**
 * Testeurs Android (admin) — migration 0732 : les adresses Google laissées sur le site par le bouton Google Play
 * (l'app Android est en test fermé). L'admin les ajoute aux testeurs dans la Play Console (« Copier les adresses »),
 * puis envoie l'invitation : un email avec le lien pour devenir testeur et celui de l'app.
 */
import { useState } from 'react';
import { Copy, Mail, Send, Trash2 } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { useAuth, isAdmin } from '@/lib/auth';
import { copyText } from '@/lib/clipboard';
import {
  filterTesters,
  testerEmailList,
  useAndroidTesterRequests,
  useDeleteAndroidTesterRequest,
  useSendAndroidTesterInvite,
  type AndroidTesterRequest,
  type TesterFilter,
} from '@/lib/admin/android-testers';

const DATE = new Intl.DateTimeFormat('fr-FR', {
  timeZone: 'Europe/Paris',
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

const PLAY_CONSOLE_URL = 'https://play.google.com/console';

function TesterCard({ row }: { row: AndroidTesterRequest }) {
  const send = useSendAndroidTesterInvite();
  const remove = useDeleteAndroidTesterRequest();
  const invited = !!row.invited_at;
  return (
    <li
      className="border-ink-200 flex flex-col gap-3 rounded-2xl border bg-white p-4"
      data-testid="tester-card"
    >
      <div className="flex flex-col gap-1">
        <p className="text-ink-900 break-all text-[15px] font-semibold">{row.email}</p>
        <p className="text-ink-500 text-sm">
          Demandé le {DATE.format(new Date(row.created_at))}
          {row.request_count > 1 && ` · ${row.request_count} demandes`}
        </p>
        <p className={cn('text-sm', invited ? 'text-green-700' : 'text-amber-700')}>
          {invited
            ? `Invitation envoyée le ${DATE.format(new Date(row.invited_at!))}${
                row.invite_count > 1 ? ` (${row.invite_count} fois)` : ''
              }`
            : 'Pas encore invitée'}
        </p>
      </div>
      {send.error && (
        <p role="alert" className="text-sm text-red-600">
          {send.error.message}
        </p>
      )}
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <button
          type="button"
          onClick={() => {
            if (window.confirm(`Retirer la demande de ${row.email} ?`)) remove.mutate(row.id);
          }}
          disabled={remove.isPending}
          className="text-ink-600 border-ink-200 inline-flex min-h-[2.75rem] items-center justify-center gap-1 rounded-xl border px-4 text-sm font-semibold active:scale-[0.98] disabled:opacity-50"
        >
          <Trash2 className="h-4 w-4" aria-hidden /> Retirer
        </button>
        <button
          type="button"
          onClick={() => send.mutate(row.id)}
          disabled={send.isPending}
          className={cn(
            'inline-flex min-h-[2.75rem] items-center justify-center gap-1 rounded-xl px-4 text-sm font-semibold active:scale-[0.98] disabled:opacity-50',
            invited
              ? 'text-ink-700 border-ink-200 border'
              : 'bg-primary-500 text-white hover:brightness-95',
          )}
        >
          <Send className="h-4 w-4" aria-hidden />
          {send.isPending ? 'Envoi…' : invited ? 'Renvoyer l’invitation' : 'Envoyer l’invitation'}
        </button>
      </div>
    </li>
  );
}

export function TutorAdminAndroidTestersPage() {
  const { profile, user } = useAuth();
  const admin = isAdmin(profile, user?.email);
  const [filter, setFilter] = useState<TesterFilter>('pending');
  const [copied, setCopied] = useState(false);
  const { data: rows = [], isLoading, isError } = useAndroidTesterRequests(admin);

  if (!admin)
    return (
      <div className="mx-auto w-full max-w-3xl px-4 py-6">
        <p className="text-ink-500 text-sm">Réservé aux admins.</p>
      </div>
    );

  const shown = filterTesters(rows, filter);
  const pending = filterTesters(rows, 'pending');
  const tabs: { id: TesterFilter; label: string }[] = [
    { id: 'pending', label: `À inviter (${pending.length})` },
    { id: 'invited', label: 'Invitées' },
    { id: 'all', label: 'Toutes' },
  ];

  async function copyPending() {
    const ok = await copyText(testerEmailList(pending));
    setCopied(ok);
    if (ok) window.setTimeout(() => setCopied(false), 2500);
  }

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:py-8">
      <header className="mb-5 flex flex-col gap-2">
        <h1 className="font-display text-ink-900 text-[24px] font-bold">Testeurs Android</h1>
        <p className="text-ink-500 font-sans text-[14px]">
          Les adresses Google laissées sur le site. Ajoute-les aux testeurs du test fermé dans la{' '}
          <a
            href={PLAY_CONSOLE_URL}
            target="_blank"
            rel="noreferrer"
            className="text-primary-600 underline"
          >
            Play Console
          </a>
          , puis envoie l’invitation : un email avec le lien pour devenir testeur et celui de l’app.
        </p>
        {pending.length > 0 && (
          <div className="flex flex-col sm:flex-row">
            <button
              type="button"
              onClick={() => void copyPending()}
              className="text-ink-700 border-ink-200 inline-flex min-h-[2.75rem] items-center justify-center gap-1 rounded-xl border bg-white px-4 text-sm font-semibold active:scale-[0.98]"
            >
              <Copy className="h-4 w-4" aria-hidden />
              {copied ? 'Adresses copiées' : `Copier les ${pending.length} adresses à inviter`}
            </button>
          </div>
        )}
      </header>

      <div role="tablist" aria-label="Statut" className="mb-4 flex gap-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={filter === t.id}
            onClick={() => setFilter(t.id)}
            className={cn(
              'min-h-[2.75rem] flex-1 rounded-xl border px-3 text-sm font-semibold sm:flex-none',
              filter === t.id
                ? 'border-primary-500 bg-primary-50 text-primary-700'
                : 'border-ink-200 text-ink-600 bg-white',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="text-ink-500 text-sm">Chargement…</p>
      ) : isError ? (
        <p className="text-sm text-red-600">Impossible de charger les demandes.</p>
      ) : shown.length === 0 ? (
        <p className="text-ink-500 flex items-center gap-2 text-sm">
          <Mail className="h-4 w-4" aria-hidden /> Aucune demande ici pour l’instant.
        </p>
      ) : (
        <ul className="flex flex-col gap-4">
          {shown.map((r) => (
            <TesterCard key={r.id} row={r} />
          ))}
        </ul>
      )}
    </div>
  );
}
